import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import PrivacyBadge from '../components/PrivacyBadge';
import client from '../api/client';

export default function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const search = new URLSearchParams(location.search);
  const redirect = search.get('next') || '/dashboard';
  const resetTokenFromUrl = search.get('reset_token') || '';
  const emailFromUrl = search.get('email') || '';

  // Modes: 'login' | 'register' | 'forgot' | 'reset'
  const [mode, setMode] = useState(resetTokenFromUrl ? 'reset' : 'login');
  const [form, setForm] = useState({
    email: emailFromUrl, password: '', partner1: '', partner2: '', weddingDate: '',
    newPassword: '', resetToken: resetTokenFromUrl,
  });
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState('');
  const { login, register, loginWithGoogle } = useAuth();
  const { toast } = useToast();

  // If arriving with a reset token in the URL, make sure the URL is clean
  // after we've consumed the token so a shared screenshot doesn't leak it.
  useEffect(() => {
    if (resetTokenFromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete('reset_token');
      url.searchParams.delete('email');
      window.history.replaceState(null, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setNotice('');
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        toast({ title: 'Welcome back — ready to plan.' });
        navigate(redirect);
      } else if (mode === 'register') {
        await register({
          email: form.email, password: form.password,
          partner1: form.partner1 || form.email.split('@')[0],
          partner2: form.partner2, weddingDate: form.weddingDate,
        });
        toast({ title: 'Your wedding workspace is ready 💝' });
        navigate('/onboarding');
      } else if (mode === 'forgot') {
        const { data } = await client.post('/account/forgot-password', { email: form.email });
        setNotice(data.message || 'If an account exists for that email, we just sent a reset link.');
      } else if (mode === 'reset') {
        if (form.newPassword.length < 6) {
          throw new Error('Password must be at least 6 characters.');
        }
        await client.post('/account/reset-password', {
          email: form.email, token: form.resetToken, new_password: form.newPassword,
        });
        toast({ title: 'Password updated. Signing you in…' });
        await login(form.email, form.newPassword);
        navigate('/dashboard');
      }
    } catch (err) {
      toast({
        title: "Hmm, that didn't work",
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const headerTitle = {
    login: 'Welcome back',
    register: 'Start your workspace',
    forgot: 'Reset your password',
    reset: 'Set a new password',
  }[mode];
  const headerSub = {
    login: 'Sign in to continue planning.',
    register: 'Just the basics — you can change everything later.',
    forgot: "We'll email you a link to pick a fresh password.",
    reset: 'Choose a strong one — this replaces your old password immediately.',
  }[mode];

  return (
    <div className="min-h-screen bg-[#FBF7F2] flex flex-col">
      <div className="p-6 lg:px-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#3A3A3A] hover:text-[#7B2C3B]">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-6 justify-center">
            <div className="w-9 h-9 rounded-full bg-[#7B2C3B] flex items-center justify-center">
              <Heart className="w-4 h-4 text-[#FBF7F2] fill-[#FBF7F2]" />
            </div>
            <span className="font-serif text-2xl font-semibold">
              <span className="text-[#7B2C3B]">Your</span>weddingfriends
            </span>
          </div>

          <div className="bg-white rounded-2xl p-7 shadow-sm">
            {mode !== 'forgot' && mode !== 'reset' && (
              <div className="flex gap-2 mb-5 bg-[#F4E4DA] rounded-full p-1">
                <button
                  type="button"
                  onClick={() => { setMode('login'); setNotice(''); }}
                  data-testid="auth-tab-login"
                  className={`flex-1 py-2 rounded-full text-sm transition-all ${mode === 'login' ? 'bg-white text-[#7B2C3B] shadow-sm' : 'text-[#7B2C3B]/70'}`}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('register'); setNotice(''); }}
                  data-testid="auth-tab-register"
                  className={`flex-1 py-2 rounded-full text-sm transition-all ${mode === 'register' ? 'bg-white text-[#7B2C3B] shadow-sm' : 'text-[#7B2C3B]/70'}`}
                >
                  Create account
                </button>
              </div>
            )}

            <h1 className="font-serif text-3xl text-[#2A2A2A] mb-1" data-testid="auth-title">{headerTitle}</h1>
            <p className="text-sm text-[#6A6A6A] mb-5">{headerSub}</p>

            {notice && (
              <div className="mb-4 p-3 rounded-xl bg-[#EFF6EF] text-[#3F6B3F] text-sm" data-testid="auth-notice">{notice}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === 'register' && (
                <div className="grid grid-cols-2 gap-3">
                  <input
                    placeholder="Your first name"
                    value={form.partner1}
                    onChange={(e) => setForm({ ...form, partner1: e.target.value })}
                    className="px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                  />
                  <input
                    placeholder="Partner's first name"
                    value={form.partner2}
                    onChange={(e) => setForm({ ...form, partner2: e.target.value })}
                    className="px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                  />
                </div>
              )}
              {mode !== 'reset' && (
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
              )}
              {(mode === 'login' || mode === 'register') && (
                <input
                  required
                  type="password"
                  minLength={6}
                  placeholder="Password (6+ characters)"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
              )}
              {mode === 'reset' && (
                <input
                  required
                  type="password"
                  minLength={6}
                  placeholder="New password (6+ characters)"
                  value={form.newPassword}
                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                  data-testid="auth-new-password"
                  className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
              )}
              {mode === 'register' && (
                <input
                  type="date"
                  value={form.weddingDate}
                  onChange={(e) => setForm({ ...form, weddingDate: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-sm text-[#3A3A3A] focus:outline-none focus:border-[#7B2C3B]"
                />
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="auth-submit"
                className="w-full btn-primary py-3 rounded-full text-sm font-medium disabled:opacity-60"
              >
                {submitting ? 'One moment…' : {
                  login: 'Sign in', register: 'Create my account',
                  forgot: 'Email me a reset link', reset: 'Set new password & sign in',
                }[mode]}
              </button>
            </form>

            {mode === 'login' && (
              <button type="button" onClick={() => { setMode('forgot'); setNotice(''); }}
                      data-testid="auth-forgot-link"
                      className="mt-4 w-full text-xs text-[#7B2C3B] hover:underline">
                Forgot your password?
              </button>
            )}
            {(mode === 'forgot' || mode === 'reset') && (
              <button type="button" onClick={() => { setMode('login'); setNotice(''); }}
                      data-testid="auth-back-to-login"
                      className="mt-4 w-full text-xs text-[#7B2C3B] hover:underline">
                ← Back to sign in
              </button>
            )}

            <p className="text-xs text-center text-[#6A6A6A] mt-5">
              By continuing you agree to our{' '}
              <Link to="/terms" className="underline hover:text-[#7B2C3B]">terms</Link> &amp;{' '}
              <Link to="/privacy" className="underline hover:text-[#7B2C3B]">privacy policy</Link>.
            </p>
          </div>
          <div className="flex justify-center mt-5">
            <PrivacyBadge />
          </div>
        </div>
      </div>
    </div>
  );
}

